import { User } from "~/common/Store/models/User";
import getLowestPrice from "./getLowestPrice";
import shouldDisplayMapStyle from "./shouldDisplayMapStyle";

interface QuantityDiscount {
  endQuantity?: number;
  isLotDiscount: boolean;
  quantity: number;
  price: number;
  startQuantity: number;
}

interface Product {
  showBelowMinimumAdvertisedPriceForLoggedInCustomers: boolean;
  showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: boolean;
  startBuy: number;
  price: {
    minimumAdvertisedPriceProperties: {
      price: number;
      type: string;
    };
    minimumAdvertisedPriceOverride: {
      priceLevel5: number;
    };
    quantityDiscounts: QuantityDiscount[];
    displaySalePrice: boolean;
    saleStartDate?: string;
    saleEndDate?: string;
    salePrice: number;
    price: number;
    displayMinimumAdvertisedPrice: boolean;
  };
}

export default (
  product: Product,
  user: User
): [boolean, QuantityDiscount[]] => {
  const {
    price,
    showBelowMinimumAdvertisedPriceForLoggedInCustomers,
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    startBuy,
  } = product;

  if (!price.quantityDiscounts || price.quantityDiscounts.length <= 0) {
    return [false, []];
  }

  const lowestPrice = getLowestPrice(
    price,
    showBelowMinimumAdvertisedPriceForLoggedInCustomers,
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    user
  );

  // Use this number to filter out lot discounts that are more expensive than what customers
  //  are already getting with non-lot discounts by buying the minimum requirement,
  //  We don't want to show prices that are more expensive at lower quantity than min buy.
  //  Ex: min buy is 100 and quantity discounts as follow:
  //    [{startQty: 2, lot: true, price: 20.90}, {startQty: 10: lot: false, price: 19.91}, {startQty: 100, lot: true, price:18.75}]
  //    If customer buys 100 to meet the minimum requirement, they will get $18.75 for all 100.
  //    If customer buys 101, they will get $18.75 for all 100 and $19.91 for 1, because it's bigger than 10.
  //    If customer buys 102, they will get $18.75 for all 100 and $19.91 for 2, because it's bigger than 10 and cheaper than the lot discount of 2 qty.
  const lowestNonLotDiscountThatUserIsAlreadyQualifiedByBuyingTheMinimum = Math.min(
    ...price.quantityDiscounts
      .filter((x) => !x.isLotDiscount && x.startQuantity < startBuy)
      .map((x) => x.price)
  );

  const discountsWithLowerPriceAndHigherStartBuy = price.quantityDiscounts
    .filter((discount) => discount.price < lowestPrice)
    .filter(
      (discount) =>
        discount.startQuantity >= startBuy ||
        (discount.isLotDiscount &&
          discount.price <
            lowestNonLotDiscountThatUserIsAlreadyQualifiedByBuyingTheMinimum) ||
        (!discount.endQuantity && !discount.isLotDiscount) ||
        (discount.endQuantity && discount.endQuantity > startBuy)
    )
    .map((x) => {
      return {
        isLotDiscount: x.isLotDiscount,
        price: x.price,
        quantity: x.quantity,
        startQuantity:
          !x.isLotDiscount && x.startQuantity < startBuy
            ? startBuy
            : x.startQuantity,
        endQuantity: x.endQuantity,
      };
    });

  const displayMapStyle = shouldDisplayMapStyle(product, user);

  const shouldDisplayQuantityDiscountTable =
    !displayMapStyle && discountsWithLowerPriceAndHigherStartBuy.length > 0;

  return [
    shouldDisplayQuantityDiscountTable,
    discountsWithLowerPriceAndHigherStartBuy,
  ];
};
